import { useState, type MouseEvent } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Button, Icon, IconButton } from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../../../models/user-generated-contents/context';
import Spinner from '../../../../../components/ui/spinner';
import './cover-image.scss';
import useMediaUploader from '../../../../../common/use-media-uploader';

const MAX_FILE_SIZE = 10485760; // 10 MB
const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_FILE_TYPES = 'image/jpeg, image/png';

type UploadingProps = { label: string };

const Uploading = ({ label }: UploadingProps) => (
  <div className="ugc-cover-image__uploading">
    <Spinner loading />
    <span className="ugc-cover-image__uploading-text text-body">{label}</span>
  </div>
);

type PreviewProps = {
  imageUrl: string;
  isDragActive: boolean;
  onOpen: () => void;
  onDelete: (e: MouseEvent) => void;
  deleteLabel: string;
  previewLabel: string;
};

const Preview = ({
  imageUrl,
  isDragActive,
  onOpen,
  onDelete,
  deleteLabel,
  previewLabel,
}: PreviewProps) => (
  <div
    role="button"
    tabIndex={0}
    className={cx('ugc-cover-image__preview', {
      'ugc-cover-image__preview--drag-active': isDragActive,
    })}
    onClick={onOpen}
    onKeyDown={(e) => {
      if (
        e.target === e.currentTarget &&
        (e.key === 'Enter' || e.key === ' ')
      ) {
        e.preventDefault();
        onOpen();
      }
    }}
    aria-label={previewLabel}
  >
    <img
      src={imageUrl}
      alt={previewLabel}
      className="ugc-cover-image__preview-img"
    />
    {isDragActive && (
      <div
        className="ugc-cover-image__preview-drag-overlay"
        data-testid="ugc-cover-image-drag-overlay"
      />
    )}
    <div className="ugc-cover-image--delete-overlay">
      <IconButton
        iconName="delete"
        size="compact"
        onClick={onDelete}
        aria-label={deleteLabel}
      />
    </div>
  </div>
);

type EmptyProps = {
  onOpen: () => void;
  dropText: string;
  orText: string;
  selectFilesLabel: string;
  maxSizeText: string;
};

const Empty = ({
  onOpen,
  dropText,
  orText,
  selectFilesLabel,
  maxSizeText,
}: EmptyProps) => (
  <>
    <span className="ugc-cover-image__drop-text text-body">{dropText}</span>
    <span className="ugc-cover-image__or text-caption">{orText}</span>
    <Button
      variant="primary"
      label={selectFilesLabel}
      size="compact"
      onClick={onOpen}
    />
    <span className="ugc-cover-image__max-size text-caption">
      {maxSizeText}
    </span>
  </>
);

const CoverImage = () => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateUserGeneratedContentFormState } =
    useUserGeneratedContent();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | undefined>();

  const imageUrl = userGeneratedContent.content_cover?.image?.url;

  const { status } = useMediaUploader({
    file,
    onStatusChange: undefined,
    onComplete: (url: string) => {
      const image = { url };
      updateUserGeneratedContentFormState({ content_cover: { image } });
      setFile(null);
    },
    onError: () => {
      setError(t('user_generated_content.cover_image.error_upload'));
      setFile(null);
    },
  });

  const isUploading = status === 'fetching' || status === 'uploading';

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: 1,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: isUploading,
    onDropAccepted: ([acceptedFile]) => {
      setError(undefined);
      setFile(acceptedFile);
    },
    onDropRejected: ([rejection]) => {
      const code = rejection?.errors[0]?.code;
      if (code === 'file-too-large') {
        setError(
          t('user_generated_content.cover_image.error_file_size', {
            maxSize: MAX_FILE_SIZE_MB,
          })
        );
      } else {
        setError(t('user_generated_content.cover_image.error_upload'));
      }
    },
  });

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    updateUserGeneratedContentFormState({ content_cover: undefined });
  };

  const renderContent = () => {
    if (isUploading) {
      return <Uploading label={t('common.uploading')} />;
    }
    if (imageUrl) {
      return (
        <Preview
          imageUrl={imageUrl}
          isDragActive={isDragActive}
          onOpen={open}
          onDelete={handleDelete}
          deleteLabel={t('user_generated_content.cover_image.delete')}
          previewLabel={t('user_generated_content.cover_image.preview')}
        />
      );
    }
    return (
      <Empty
        onOpen={open}
        dropText={t('user_generated_content.cover_image.drop_text')}
        orText={t('common.or')}
        selectFilesLabel={t('user_generated_content.cover_image.select_files')}
        maxSizeText={t('user_generated_content.cover_image.max_size', {
          maxSize: MAX_FILE_SIZE_MB,
        })}
      />
    );
  };

  return (
    <div
      className="ugc-cover-image"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <p className="ugc-cover-image__label text-body">
        {t('user_generated_content.cover_image.label')}
      </p>

      <div
        {...getRootProps({
          className: cx('ugc-cover-image__dropzone', {
            'ugc-cover-image__dropzone--preview': !!imageUrl && !isUploading,
            'ugc-cover-image__dropzone--empty': !imageUrl && !isUploading,
            'ugc-cover-image__dropzone--drag-active': isDragActive && !imageUrl,
          }),
          'data-testid': 'ugc-cover-image-dropzone',
        })}
      >
        <input {...getInputProps()} />
        {renderContent()}
      </div>
      {error && (
        <span
          role="alert"
          className="text-caption mt-1 flex items-center gap-2 text-negative"
        >
          <Icon size={16}>error</Icon> {error}
        </span>
      )}
    </div>
  );
};

export default CoverImage;
